import axios from 'axios';
import { storeToken, clearToken, isTokenExpired } from '../utils/tokenManager';
import { getSSOURL } from '../config/api.config';

/**
 * Decode framework token using the IAS token API
 * @param {string} token - The framework token to decode
 * @returns {Promise<Object>} - The decoded token response
 */
export const decodeFrameworkToken = async (token) => {
  try {
    const response = await axios.get(`${getSSOURL()}/ias/Token`, {
      headers: {
        'ias-upmToken': token
      }
    });
    
    return response.data;
    
  } catch (error) {
    console.error('Token decode error:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
};

/**
 * Get and decode the stored framework token from localStorage
 * @returns {Promise<Object|null>} - The decoded token response or null if no token
 */
export const decodeStoredToken = async () => {
  // Check if token is expired first
  if (isTokenExpired()) {
    clearToken();
    return null;
  }
  
  const token = localStorage.getItem('frameworkToken');
  
  if (!token) {
    return null;
  }
  
  try {
    const decodedData = await validateTokenAndAccess(token);
    
    // Store the decoded token data and update expiry
    localStorage.setItem('decodedTokenData', JSON.stringify(decodedData));
    storeToken(token, decodedData); // Update expiry information
    
    // Extract and store user email
    extractUserEmail(decodedData);

    // Store resolved role for http.client.js to attach as X-User-Role header
    const role = getUserRole(decodedData);
    if (role) {
      localStorage.setItem('userRole', role);
    }
    
    return decodedData;
  } catch (error) {
    console.error('Failed to decode stored token or access denied:', error);
    clearToken();
    return null;
  }
};

/**
 * Get the cached decoded token data from localStorage
 * @returns {Object|null} - The cached decoded token data or null if not found
 */
export const getCachedDecodedToken = () => {
  try {
    const cachedData = localStorage.getItem('decodedTokenData');
    return cachedData ? JSON.parse(cachedData) : null;
  } catch (error) {
    console.error('Failed to parse cached decoded token data:', error);
    return null;
  }
};

/**
 * Extract and store user email from decoded token
 * @param {Object} decodedTokenData - The decoded token response
 * @returns {string|null} - The user's email or null if not found
 */
export const extractUserEmail = (decodedTokenData) => {
  try {
    const email = decodedTokenData?.Email || decodedTokenData?.email;
    if (email) {
      localStorage.setItem('userEmail', email);
      return email;
    }
    return null;
  } catch (error) {
    console.error('Failed to extract user email:', error);
    return null;
  }
};

/**
 * Get stored user email from localStorage
 * @returns {string|null} - The stored user email or null if not found
 */
export const getStoredUserEmail = () => {
  try {
    return localStorage.getItem('userEmail');
  } catch (error) {
    console.error('Failed to get stored user email:', error);
    return null;
  }
};

/**
 * Get user email from decoded token data (either from cache or by decoding stored token)
 * @returns {Promise<string|null>} - The user's email or null if not available
 */
export const getUserEmail = async () => {
  // First try to get stored email
  let email = getStoredUserEmail();
  if (email) {
    return email;
  }

  // If no stored email, try to decode token and extract email
  try {
    const decodedData = await decodeStoredToken();
    if (decodedData) {
      email = extractUserEmail(decodedData);
    }
    return email;
  } catch (error) {
    console.error('Failed to get user email:', error);
    return null;
  }
};

/**
 * Check if the framework token exists and is valid with required access
 * @returns {Promise<boolean>} - True if token exists, is valid, and has YDqe access, false otherwise
 */
export const isTokenValid = async () => {
  try {
    // First check if token is expired
    if (isTokenExpired()) {
      clearToken();
      return false;
    }
    
    const token = localStorage.getItem('frameworkToken');
    
    if (!token) {
      return false;
    }

    // First try to use cached decoded token data (faster, no API call)
    const cachedData = getCachedDecodedToken();
    if (cachedData) {
      if (isAdminUser(cachedData) || hasYDqeComponentAccess(cachedData)) {
        return true;
      }
    }

    // If no cache or cache doesn't have access, validate with API
    const decodedData = await validateTokenAndAccess(token);
    
    // Update token expiry after successful validation
    storeToken(token, decodedData);
    
    return true;
    
  } catch (error) {
    console.error('Token validation failed:', error);
    
    // If token validation fails, clear invalid token data
    clearToken();
    
    return false;
  }
};

/**
 * Special ComponentId that grants access to everything. If any role assigned
 * to the user has a component with this ID, every `hasComponentAccess` check
 * short-circuits to true (admin override).
 */
export const ADMIN_COMPONENT_ID = '0000';

// ── Business role ComponentIds (must match backend middleware.py) ──────────
export const ROLE_COMPONENT_IDS = Object.freeze({
  ADMIN: 'ADMN',        // Super Admin
  MANAGER: 'MNGR',      // Ideation Manager
  IDEA_OWNER: 'IOWN',   // Idea Owner / submitter
});

export const VIEW_AS_ROLE_KEY = 'viewAsRole';

/**
 * Derive the user's primary business role from the decoded token.
 * Priority: ADMN > MNGR > IOWN.
 * Returns null if none of the role ComponentIds is present.
 */
export const getUserRole = (decodedTokenData) => {
  if (!decodedTokenData) return null;
  const { ADMIN, MANAGER, IDEA_OWNER } = ROLE_COMPONENT_IDS;
  if (hasComponentAccess(decodedTokenData, ADMIN)) return ADMIN;
  if (hasComponentAccess(decodedTokenData, MANAGER)) return MANAGER;
  if (hasComponentAccess(decodedTokenData, IDEA_OWNER)) return IDEA_OWNER;

  // Fallback for environments where RoleName is configured but ComponentIds
  // ADMN/MNGR/IOWN are not yet provisioned in IAS.
  try {
    const roles = Array.isArray(decodedTokenData?.Roles) ? decodedTokenData.Roles : [];
    const names = roles
      .map((r) => String(r?.RoleName || '').toLowerCase())
      .filter(Boolean);

    if (names.some((n) => n.includes('admin'))) return ADMIN;
    if (names.some((n) => n.includes('manager'))) return MANAGER;
    if (names.some((n) => n.includes('idea') || n.includes('owner') || n.includes('service'))) {
      return IDEA_OWNER;
    }
  } catch {
    // Ignore fallback parsing issues and return null below.
  }

  return null;
};

/** Returns true when the user has the Super Admin (ADMN) role. */
export const isAdminRole = (decodedTokenData) =>
  hasComponentAccess(decodedTokenData, ROLE_COMPONENT_IDS.ADMIN);

/** Returns true when the user has the Manager (MNGR) role. */
export const isManagerRole = (decodedTokenData) =>
  hasComponentAccess(decodedTokenData, ROLE_COMPONENT_IDS.MANAGER);

/** Returns true when the user has the Idea Owner (IOWN) role. */
export const isIdeaOwnerRole = (decodedTokenData) =>
  hasComponentAccess(decodedTokenData, ROLE_COMPONENT_IDS.IDEA_OWNER);

/**
 * Get the cached user role from localStorage (fast, no API call).
 * Returns null if not yet resolved.
 */
export const getCachedUserRole = () => {
  const tokenData = getCachedDecodedToken();
  return tokenData ? getUserRole(tokenData) : null;
};

/**
 * Set temporary role-preview for admin users only (development/testing aid).
 */
export const setViewAsRole = (role) => {
  const allowed = Object.values(ROLE_COMPONENT_IDS);
  if (!allowed.includes(role)) return;
  localStorage.setItem(VIEW_AS_ROLE_KEY, role);
};

export const getViewAsRole = () => {
  const value = localStorage.getItem(VIEW_AS_ROLE_KEY);
  const allowed = Object.values(ROLE_COMPONENT_IDS);
  return allowed.includes(value) ? value : null;
};

/**
 * Effective role used by UI and backend header forwarding.
 * Admin can switch preview to manager/owner for live testing.
 */
export const getEffectiveUserRole = () => {
  const actual = getCachedUserRole();
  if (actual !== ROLE_COMPONENT_IDS.ADMIN) return actual;
  return getViewAsRole() || actual;
};

/**
 * Check if the user has the admin ComponentId (`0000`) on any role.
 * @param {Object} decodedTokenData - The decoded token response
 * @returns {boolean}
 */
export const isAdminUser = (decodedTokenData) => {
  try {
    if (!decodedTokenData?.Roles || !Array.isArray(decodedTokenData.Roles)) {
      return false;
    }
    for (const role of decodedTokenData.Roles) {
      if (!Array.isArray(role?.Components)) continue;
      if (role.Components.some((c) => c && c.ComponentId === ADMIN_COMPONENT_ID)) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error checking admin component access:', error);
    return false;
  }
};

/**
 * Check if user has access to a specific framework ComponentId.
 *
 * The token carries `Roles[].Components[].ComponentId`. Returns true if any
 * role grants the requested ComponentId. Pass an array of IDs to check
 * "any-of". Users with the admin ComponentId (`0000`) always pass.
 *
 * @param {Object} decodedTokenData - The decoded token response
 * @param {string|string[]} componentId - One ComponentId or an array of IDs
 * @returns {boolean}
 */
export const hasComponentAccess = (decodedTokenData, componentId) => {
  try {
    if (!decodedTokenData?.Roles || !Array.isArray(decodedTokenData.Roles)) {
      return false;
    }
    const wanted = Array.isArray(componentId) ? componentId : [componentId];
    if (wanted.length === 0) return false;

    for (const role of decodedTokenData.Roles) {
      if (!Array.isArray(role?.Components)) continue;
      const granted = role.Components.some(
        (c) => c && (c.ComponentId === ADMIN_COMPONENT_ID || wanted.includes(c.ComponentId))
      );
      if (granted) return true;
    }
    return false;
  } catch (error) {
    console.error('Error checking component access:', error);
    return false;
  }
};

/**
 * Check if user has required YDqe component access
 * @param {Object} decodedTokenData - The decoded token response
 * @returns {boolean} - True if user has YDqe component access, false otherwise
 */
export const hasYDqeComponentAccess = (decodedTokenData) => {
  return hasComponentAccess(decodedTokenData, 'YDqe');
};

/**
 * Validate token and check for required YDqe component access
 * @param {string} token - The framework token to validate
 * @returns {Promise<Object>} - The decoded token response if valid and has access
 * @throws {Error} - If token is invalid or user lacks required access
 */
export const validateTokenAndAccess = async (token) => {
  try {
    // First decode the token
    const decodedData = await decodeFrameworkToken(token);
    
    if (!decodedData) {
      throw new Error('Failed to decode token');
    }

    // Allow login if the user has the admin component (0000) OR the
    // legacy YDqe component. Per-area access is gated separately by the
    // component IDs declared in `constants/componentAccess.js`.
    if (!isAdminUser(decodedData) && !hasYDqeComponentAccess(decodedData)) {
      throw new Error('Access denied. Contact SWT team for Idea analysis Tool.');
    }

    return decodedData;
  } catch (error) {
    console.error('Token validation or access check failed:', error);
    throw error;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  clearToken();
  localStorage.removeItem('userRole');
  localStorage.removeItem(VIEW_AS_ROLE_KEY);
};